import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  fetchWorkspaceContext,
  fetchWorkspaces,
  getApiWorkspaceId,
  setApiWorkspaceId,
  putDefaultWorkspace,
  type WorkspaceListItem,
} from '../api'
import type { SharedAccessPermissions } from '../sharedAccessPermissions'
import { can } from '../sharedAccessPermissions'

type Ctx = {
  ready: boolean
  error: string
  workspaces: WorkspaceListItem[]
  currentWorkspaceId: string | null
  defaultWorkspaceId: string | null
  /** Logged-in Telegram user (Mini App). */
  actorTelegramUserId: number | null
  role: 'owner' | 'member' | null
  ownerTelegramUserId: number | null
  permissions: SharedAccessPermissions | null
  setWorkspace: (id: string) => void
  setDefaultWorkspace: (id: string) => Promise<void>
  refresh: () => Promise<void>
  can: (section: keyof SharedAccessPermissions, key: string) => boolean
}

const WorkspaceStateContext = createContext<Ctx | null>(null)

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false)
  const [error, setError] = useState('')
  const [workspaces, setWorkspaces] = useState<WorkspaceListItem[]>([])
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string | null>(getApiWorkspaceId)
  const [defaultWorkspaceId, setDefaultWorkspaceId] = useState<string | null>(null)
  const [actorTelegramUserId, setActorTelegramUserId] = useState<number | null>(null)
  const [role, setRole] = useState<'owner' | 'member' | null>(null)
  const [ownerTelegramUserId, setOwnerTelegramUserId] = useState<number | null>(null)
  const [permissions, setPermissions] = useState<SharedAccessPermissions | null>(null)
  const requestVersion = useRef(0)
  const activeRequest = useRef<AbortController | null>(null)
  const preferenceRequest = useRef<AbortController | null>(null)
  const initialized = useRef(false)

  const load = useCallback(async () => {
    const version = ++requestVersion.current
    activeRequest.current?.abort()
    const controller = new AbortController()
    activeRequest.current = controller
    try {
      setError('')
      const ws = await fetchWorkspaces(controller.signal)
      if (version !== requestVersion.current) return
      setWorkspaces(ws.workspaces)
      setDefaultWorkspaceId(ws.defaultWorkspaceId)
      const allowed = new Set(ws.workspaces.map((w) => w.id))
      // A new app launch uses the account preference, not the last device-local
      // selection. Refreshes within this session preserve explicit navigation.
      let current = initialized.current ? getApiWorkspaceId() || ws.currentWorkspaceId : ws.defaultWorkspaceId
      if (!current || !allowed.has(current)) {
        const personal = ws.workspaces.find((w) => w.role === 'owner')
        current = personal ? personal.id : ws.currentWorkspaceId
      }
      if (current && allowed.has(current)) {
        setApiWorkspaceId(current)
      } else {
        setApiWorkspaceId(ws.currentWorkspaceId)
      }
      setCurrentWorkspaceId(getApiWorkspaceId())
      const ctx = await fetchWorkspaceContext(controller.signal)
      if (version !== requestVersion.current) return
      setActorTelegramUserId(ctx.actorTelegramUserId)
      setRole(ctx.role)
      setOwnerTelegramUserId(ctx.ownerTelegramUserId)
      setPermissions(ctx.permissions)
      initialized.current = true
      setReady(true)
    } catch (error) {
      if (controller.signal.aborted || version !== requestVersion.current) return
      throw error
    }
  }, [])

  useEffect(() => {
    let alive = true
    const t = window.setTimeout(() => {
      void load()
        .catch((e: unknown) => {
          if (alive) {
            setError(String(e instanceof Error ? e.message : e))
            setReady(true)
          }
        })
    }, 0)
    return () => {
      alive = false
      requestVersion.current += 1
      activeRequest.current?.abort()
      preferenceRequest.current?.abort()
      window.clearTimeout(t)
    }
  }, [load])

  const setDefaultWorkspace = useCallback(async (id: string) => {
    preferenceRequest.current?.abort()
    const controller = new AbortController()
    preferenceRequest.current = controller
    try {
      const result = await putDefaultWorkspace(id, controller.signal)
      if (!controller.signal.aborted) setDefaultWorkspaceId(result.defaultWorkspaceId)
    } finally {
      if (preferenceRequest.current === controller) preferenceRequest.current = null
    }
  }, [])

  const setWorkspace = useCallback(
    (id: string) => {
      const version = ++requestVersion.current
      activeRequest.current?.abort()
      const controller = new AbortController()
      activeRequest.current = controller
      setReady(false)
      setPermissions(null)
      setRole(null)
      setOwnerTelegramUserId(null)
      setApiWorkspaceId(id)
      setCurrentWorkspaceId(id)
      void fetchWorkspaceContext(controller.signal)
        .then((ctx) => {
          if (version !== requestVersion.current) return
          setActorTelegramUserId(ctx.actorTelegramUserId)
          setRole(ctx.role)
          setOwnerTelegramUserId(ctx.ownerTelegramUserId)
          setPermissions(ctx.permissions)
          setError('')
          setReady(true)
        })
        .catch(async (e: unknown) => {
          if (controller.signal.aborted || version !== requestVersion.current) return
          setError(String(e instanceof Error ? e.message : e))
          try { await load() } catch (loadError: unknown) {
            if (version + 1 === requestVersion.current) {
              setError(String(loadError instanceof Error ? loadError.message : loadError))
              setReady(true)
            }
          }
        })
    },
    [load],
  )

  const canPerm = useCallback(
    (section: keyof SharedAccessPermissions, key: string) => {
      return can(permissions, section, key)
    },
    [permissions],
  )

  const value = useMemo<Ctx>(
    () => ({
      ready,
      error,
      workspaces,
      currentWorkspaceId,
      defaultWorkspaceId,
      actorTelegramUserId,
      role,
      ownerTelegramUserId,
      permissions,
      setWorkspace,
      setDefaultWorkspace,
      refresh: load,
      can: canPerm,
    }),
    [ready, error, workspaces, currentWorkspaceId, defaultWorkspaceId, actorTelegramUserId, role, ownerTelegramUserId, permissions, setWorkspace, setDefaultWorkspace, load, canPerm],
  )

  return <WorkspaceStateContext.Provider value={value}>{children}</WorkspaceStateContext.Provider>
}

/** @throws if used outside {@link WorkspaceProvider} */
// eslint-disable-next-line react-refresh/only-export-components -- hook is co-located with provider
export function useWorkspace() {
  const c = useContext(WorkspaceStateContext)
  if (!c) {
    throw new Error('useWorkspace must be used within WorkspaceProvider')
  }
  return c
}
